import React from 'react'
import { Box, SelectChangeEvent, StandardTextFieldProps, formControlClasses, outlinedInputClasses, selectClasses } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Field, WrappedFieldProps } from 'redux-form'
import { MaterialFieldProps } from './Material/MaterialField'
import { TextField } from '@carsys/ui'
import { cleanPhoneNumber, findCountryAreaCodeByDialCode, findPhoneNumberAreaCode } from './PhoneNumber/utils'
import { CountryAreaCodeSelect } from './PhoneNumber/CountryAreaCodeSelect'
import { formatPhonenumberByCountryConvention } from './PhoneNumber/formatPhonenumberByCountryConvention'
import { CountryCode } from '@carsys/enums/country-code'
import { useFieldError } from './Material/hooks/useHelperText'

interface ReduxInputProps extends MaterialFieldProps<StandardTextFieldProps> {
  defaultCountry: CountryCode
  labelTranslationKey?: string
  validator?: (value?: string) => string | void
}

const PhoneNumberInputComponent: React.FC<ReduxInputProps & WrappedFieldProps> = ({
  labelTranslationKey = 'setting_my_shop.telephonenumber',
  defaultCountry,
  sx,
  required = false,
  input,
  disabled,
  name,
  validator,
  onChange,
  ...rest
}) => {
  const { t } = useTranslation()

  const [areaCode, setAreaCode] = React.useState(findPhoneNumberAreaCode(defaultCountry, input.value || ''))
  const [phoneNumber, setPhoneNumber] = React.useState(cleanPhoneNumber(input.value, areaCode, defaultCountry))
  const [helperText, setHelperText] = React.useState('')
  const { isError, errorText } = useFieldError(rest.meta)

  const handleOnFocusPhoneNumber = () => {
    if (input.value) {
      const formatOnFocus = formatPhonenumberByCountryConvention(areaCode.code, input.value)
      input.onChange(formatOnFocus)
      return
    }
    if (!cleanPhoneNumber(input.value, areaCode, defaultCountry)) {
      input.onChange(areaCode.dialCode)
    }
  }

  const handleOnBlurPhoneNumber = () => {
    const cleaned = cleanPhoneNumber(input.value, areaCode, defaultCountry)
    if (!cleaned) {
      input.onChange('')
      return
    }
    input.onChange(formatPhonenumberByCountryConvention(defaultCountry, `${areaCode.dialCode}${cleaned}`))
  }

  // we change the phonenumber, not the areacode
  const handleOnchangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const areaCodeChangeCheck = findPhoneNumberAreaCode(areaCode.code, event.target.value)
    // areaCode has changed in the phonenumber, so we update the dropdown through setUpdateAreaCode
    if (areaCodeChangeCheck && areaCodeChangeCheck.dialCode !== areaCode.dialCode) {
      setAreaCode(areaCodeChangeCheck)
    }
    setPhoneNumber(event.target.value)
    if (validator) {
      if (required && !input.value) {
        setHelperText('')
      } else {
        setHelperText(t(validator(input.value) || ''))
      }
    }

    // we only run the onchange with a different value if we find either a new areaCode through the event
    // or if the formatter results in a new (formatted) string
    const formatPhonenumber = formatPhonenumberByCountryConvention(defaultCountry, event.target.value)
    input.onChange(formatPhonenumber)
    onChange && onChange(event)
  }

  const handleOnchangeAreaCode = (event: SelectChangeEvent<string>) => {
    input.onChange(
      formatPhonenumberByCountryConvention(
        defaultCountry,
        `${event.target.value}${cleanPhoneNumber(phoneNumber, areaCode, defaultCountry)}`
      )
    )
    setAreaCode(findCountryAreaCodeByDialCode(areaCode.code, event.target.value))
    onChange && onChange(event as React.ChangeEvent)
  }

  React.useEffect(() => {
    if (input.value !== '' && !disabled) {
      input.onChange(input.value)
    }
  }, [])

  return (
    <Box
      sx={{
        ...sx,
        [`.ui.form & .${selectClasses.select}`]: { py: '0.58035714em' },
        display: 'flex !important',
        [`& > .${formControlClasses.root}`]: { flex: '1' }
      }}
    >
      <CountryAreaCodeSelect onChange={handleOnchangeAreaCode} disabled={disabled} areaCode={areaCode} />
      <TextField
        data-testid='phone-number-textfield'
        sx={{
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderRadius: Theme => `0 ${Theme.shape.borderRadius}px ${Theme.shape.borderRadius}px 0`
          }
        }}
        label={t(labelTranslationKey)}
        required={required}
        disabled={disabled}
        value={input.value}
        onFocus={handleOnFocusPhoneNumber}
        onBlur={handleOnBlurPhoneNumber}
        onChange={handleOnchangePhoneNumber}
        helperText={helperText || errorText || ''}
        error={!!helperText || isError}
        inputProps={{
          'data-testid': 'phone-number-textfield-input',
          maxLength: 18
        }}
        {...rest}
      />
    </Box>
  )
}

export const PhoneNumberInputRedux: React.FC<ReduxInputProps> = props => {
  const { name, validate, placeholder, onChange, disabled, ...rest } = props
  return (
    <Field
      disabled={disabled}
      validate={validate ? [validate] : undefined}
      name={name}
      placeholder={placeholder}
      component={PhoneNumberInputComponent}
      variant='outlined'
      onChange={onChange}
      props={{ ...rest }}
    />
  )
}
